import {
	hideLoadingScreen,
	modal,
	router,
	setPageContent,
	showLoadingScreen,
} from "..";
import { getPrices } from "../api/prices";
import {
	putSmartplug,
	resetSmartplugConsumption,
	unregisterSmartplug,
} from "../api/smartplugs_{imei}";
import {
	nameField,
	ownerField,
	sharingField,
	subscriptionField,
	tagsField,
} from "../device/device_settings_fields";
import { openUnregisterDeviceModal } from "../device/unregister_device_modal";
import { locale } from "../locale";
import { state } from "../state";
import { indexToWeekday } from "../utils";
import { Bulma } from "../view/bulma";
import { centeredBox, errorToast } from "../view/components";
import { MaterialDesign } from "../view/material_design";

/**
 * @type {import('navigo').Handler}
 */
async function smartplugSettingsPage(ctx) {
	if (!ctx || !ctx.data || !ctx.data.imei) throw "missing ctx.data.imei";
	const sp = state.smartplugs[ctx.data.imei];

	const user = await state.getActiveUser();
	const currency = user.Country === "SE" ? "SEK" : "EUR";
	const isOwner = user.Email === sp.Metadata.OwnerEmail;

	const { SmartplugMonth, SmartplugYear } = await getPrices();

	const beep = soundSignalField(sp);
	const powerFailureAlarm = powerFailureAlarmField(sp);
	const loadAlarm = loadAlarmField(sp);
	const temperatureAlarm = temperatureAlarmField(sp);
	const timer = timerField(sp);
	const thermostat = thermostatField(sp);
	const subscription = subscriptionField(
		sp.Subscription,
		[SmartplugMonth, SmartplugYear],
		currency,
		isOwner,
	);
	const sharing = sharingField(sp.Metadata);
	const tags = tagsField(sp.Metadata);
	const name = nameField(sp.Metadata.Name);
	const owner = ownerField(sp.Metadata, isOwner);

	setPageContent(
		centeredBox(
			"small",
			Bulma.title(`SmartPlug - ${sp.Metadata.Name}`, "is-4"),
			Bulma.subtitle(`IMEI: ${sp.Imei}`, "is-6"),
			consumptionField(ctx, sp),
			beep.element,
			powerFailureAlarm.element,
			loadAlarm.element,
			temperatureAlarm.element,
			timer.element,
			thermostat.element,
			subscription.element,
			sharing.element,
			tags.element,
			name.element,
			owner.element,
			document.createElement("hr"),
			Bulma.buttons(
				Bulma.button(
					{
						onClick: onClickSave,
						styles: ["is-primary"],
					},
					locale.strings.save,
				),
				Bulma.button(
					{
						onClick: () => router.jump(`/devices?id=${sp.Imei}`),
						styles: ["is-primary"],
					},
					locale.strings.back,
				),
				Bulma.button(
					{
						disabled: !isOwner,
						onClick: () =>
							openUnregisterDeviceModal("SmartPlug", unregister),
						styles: ["is-danger"],
					},
					locale.strings.unregister,
				),
			),
		),
	);

	async function onClickSave() {
		showLoadingScreen();
		putSmartplug(sp.Imei, {
			BeepEnabled: beep.isOn(),
			PowerFailureAlarmEnabled: powerFailureAlarm.isOn(),
			LoadAlarm: {
				Enabled: loadAlarm.isOn(),
				Lower: loadAlarm.getLower(),
				Upper: loadAlarm.getUpper(),
			},
			TemperatureRangeAlarm: {
				Enabled: temperatureAlarm.isOn(),
				Lower: temperatureAlarm.getLower(),
				Upper: temperatureAlarm.getUpper(),
			},
			Timer: {
				Enabled: timer.isOn(),
				Off: timer.getOffSchedules(),
				On: timer.getOnSchedules(),
			},
			Thermostat: {
				Enabled: thermostat.isOn(),
				Triggers: thermostat.getTriggers(),
			},
			PriceId: subscription.getPriceId(),
			Access: sharing.getAccess(),
			Notify: sharing.getNotify(),
			Tags: tags.getValue(),
			Name: name.getValue(),
			Owner: owner.getValue(),
		})
			.then((sp) => {
				state.smartplugs[sp.Imei] = sp;
				router.jump(`/devices?id=${sp.Imei}`);
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	}

	function unregister() {
		showLoadingScreen();
		unregisterSmartplug(sp.Imei)
			.then(() => {
				delete state.smartplugs[sp.Imei];
				router.jump("/devices");
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	}
}

/**
 * @param {Smartplug} sp
 */
function soundSignalField(sp) {
	const select = Bulma.select({
		options: [
			Bulma.option(locale.strings.on, "on", sp.BeepEnabled),
			Bulma.option(locale.strings.off, "off", !sp.BeepEnabled),
		],
	});

	const field = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: "Device sound signal",
				deDE: "Gerätetonsignal",
				frFR: "Signal sonore de l'appareil",
				svSE: "Enhet ljudsignal",
			}),
		),
		Bulma.control({}, select.element),
	);

	return {
		element: field,
		isOn: () => select.getValue() === "on",
	};
}

/**
 * @param {Smartplug} sp
 */
function powerFailureAlarmField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const select = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(
				locale.strings.on,
				"on",
				isSubscribed && sp.PowerFailureAlarm.Enabled,
			),
			Bulma.option(
				locale.strings.off,
				"off",
				!isSubscribed || !sp.PowerFailureAlarm.Enabled,
			),
		],
	});

	const field = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: "Power failure alarm",
				deDE: "Alarm bei Stromausfall",
				frFR: "Alarme de panne de courant",
				svSE: "Strömavbrottslarm",
			}),
		),
		Bulma.control({}, select.element),
	);

	return {
		element: field,
		isOn: () => select.getValue() === "on",
	};
}

/**
 * @param {import('navigo').Match} ctx
 * @param {Smartplug} sp
 */
function consumptionField(ctx, sp) {
	const field = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: `Power consumption: ${sp.Consumption} Wh`,
				deDE: `Stromverbrauch: ${sp.Consumption} Wh`,
				frFR: `Consommation électrique : ${sp.Consumption} Wh`,
				svSE: `Strömförbrukning: ${sp.Consumption} Wh`,
			}),
		),
		Bulma.control(
			{},
			Bulma.button(
				{
					onClick: onClickReset,
					styles: ["is-primary"],
				},
				locale.string({
					enGB: "Reset",
					deDE: "Zurücksetzen",
					frFR: "Réinitialiser",
					svSE: "Återställ",
				}),
			),
		),
	);

	async function onClickReset() {
		showLoadingScreen();
		resetSmartplugConsumption(sp.Imei)
			.then((newSp) => {
				state.smartplugs[sp.Imei] = newSp;
				smartplugSettingsPage(ctx);
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	}

	return field;
}

/**
 * @param {Smartplug} sp
 */
function loadAlarmField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const onOff = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(
				locale.strings.on,
				"on",
				isSubscribed && sp.LoadAlarm.Enabled,
			),
			Bulma.option(
				locale.strings.off,
				"off",
				!isSubscribed || !sp.LoadAlarm.Enabled,
			),
		],
	});

	const lower = Bulma.input({
		type: "text",
		onInput: () => {
			const rawLower = lower.getValue();
			if (!rawLower) return;

			const sanitizedLower = rawLower.replace(/(?!^)-|[^-\d]/g, "");
			if (sanitizedLower.length < 2) {
				lower.setValue(sanitizedLower);
				return;
			}

			const parsedLower = +sanitizedLower;
			if (parsedLower < 0) {
				lower.setValue("0");
				return;
			}

			const rawUpper = upper.getValue();
			const parsedUpper = rawUpper ? +rawUpper : 3700;
			if (parsedLower >= parsedUpper) {
				lower.setValue((parsedUpper - 1).toString());
				return;
			}

			lower.setValue(parsedLower.toString());
		},
	});

	const upper = Bulma.input({
		type: "text",
		onInput: () => {
			const rawUpper = upper.getValue();
			if (!rawUpper) return;

			const sanitizedUpper = rawUpper.replace(/(?!^)-|[^-\d]/g, "");
			if (sanitizedUpper.length < 2) {
				upper.setValue(sanitizedUpper);
				return;
			}

			const parsedUpper = +sanitizedUpper;
			if (parsedUpper > 3700) {
				upper.setValue("3700");
				return;
			}

			const rawLower = lower.getValue();
			const parsedLower = rawLower ? +rawLower : 0;
			if (parsedUpper <= parsedLower) {
				upper.setValue((parsedLower + 1).toString());
				return;
			}

			upper.setValue(parsedUpper.toString());
		},
	});

	const openEditModal = () => {
		modal.open({
			header: locale.string({
				enGB: "Edit load alarm",
				deDE: "Lastalarm bearbeiten",
				frFR: "Modifier l'alarme de charge",
				svSE: "Redigiera lastlarm",
			}),
			body: [
				Bulma.field(
					{},
					Bulma.label(
						locale.string({
							enGB: "Lowest expected load (min 0 W)",
							deDE: "Niedrigste erwartete Last (min 0 W)",
							frFR: "Charge attendue la plus faible (min 0 W)",
							svSE: "Lägsta förväntade belastning (min 0 W)",
						}),
					),
					Bulma.control({}, lower.element),
				),
				Bulma.field(
					{},
					Bulma.label(
						locale.string({
							enGB: "Highest expected load (max 3700 W)",
							deDE: "Höchste erwartete Last (max. 3700 W)",
							frFR: "Charge maximale prévue (max. 3700 W)",
							svSE: "Högsta förväntade belastning (max 3700 W)",
						}),
					),
					Bulma.control({}, upper.element),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: () => modal.close(),
						styles: ["is-primary"],
					},
					locale.strings.done,
				),
			],
		});
	};

	const field = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: "Load alarm",
				deDE: "Lastalarm",
				frFR: "Alarme de charge",
				svSE: "Lastlarm",
			}),
		),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOff.element),
			Bulma.control(
				{},
				Bulma.button(
					{
						onClick: openEditModal,
						styles: ["is-primary"],
					},
					locale.strings.edit,
				),
			),
			Bulma.control(
				{},
				Bulma.input({
					type: "text",
					value: `${sp.LoadAlarm.Lower} - ${sp.LoadAlarm.Upper} W`,
					styles: ["is-static"],
				}).element,
			),
		),
	);

	return {
		element: field,
		isOn: () => onOff.getValue() === "on",
		getLower: () => +lower.getValue(),
		getUpper: () => +upper.getValue(),
	};
}

/**
 * @param {Smartplug} sp
 */
function temperatureAlarmField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const onOff = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(
				locale.strings.on,
				"on",
				isSubscribed && sp.TemperatureRangeAlarm.Enabled,
			),
			Bulma.option(
				locale.strings.off,
				"off",
				!isSubscribed || !sp.TemperatureRangeAlarm.Enabled,
			),
		],
	});

	const lower = Bulma.input({
		type: "text",
		onInput: () => {
			const rawLower = lower.getValue();
			if (!rawLower) return;

			const sanitizedLower = rawLower.replace(/(?!^)-|[^-\d]/g, "");
			if (sanitizedLower.length < 2) {
				lower.setValue(sanitizedLower);
				return;
			}

			const parsedLower = +sanitizedLower;
			if (parsedLower < -20) {
				lower.setValue("-20");
				return;
			}

			const rawUpper = upper.getValue();
			const parsedUpper = rawUpper ? +rawUpper : 70;
			if (parsedLower >= parsedUpper) {
				lower.setValue((parsedUpper - 1).toString());
				return;
			}

			lower.setValue(parsedLower.toString());
		},
	});

	const upper = Bulma.input({
		type: "text",
		onInput: () => {
			const rawUpper = upper.getValue();
			if (!rawUpper) return;

			const sanitizedUpper = rawUpper.replace(/(?!^)-|[^-\d]/g, "");
			if (sanitizedUpper.length < 2) {
				upper.setValue(sanitizedUpper);
				return;
			}

			const parsedUpper = +sanitizedUpper;
			if (parsedUpper > 70) {
				upper.setValue("70");
				return;
			}

			const rawLower = lower.getValue();
			const parsedLower = rawLower ? +rawLower : -20;
			if (parsedUpper <= parsedLower) {
				upper.setValue((parsedLower + 1).toString());
				return;
			}

			upper.setValue(parsedUpper.toString());
		},
	});

	const openEditModal = () => {
		modal.open({
			header: locale.string({
				enGB: "Edit temperature alarm",
				deDE: "Temperaturalarm bearbeiten",
				frFR: "Modifier l'alarme de température",
				svSE: "Redigera temperaturlarm",
			}),
			body: [
				Bulma.field(
					{},
					Bulma.control({}, lower.element),
					Bulma.help(
						locale.string({
							enGB: "Enter the lowest expected temperature (min -20 °C)",
							deDE: "Geben Sie die niedrigste erwartete Temperatur ein (min. -20 °C)",
							frFR: "Saisir la température minimale prévue (min -20 °C)",
							svSE: "Ange den lägsta förväntade temperaturen (min -20 °C)",
						}),
					),
				),
				Bulma.field(
					{},
					Bulma.control({}, upper.element),
					Bulma.help(
						locale.string({
							enGB: "Enter the highest expected temperature (max 70 °C)",
							deDE: "Geben Sie die höchste erwartete Temperatur ein (max. 70 °C)",
							frFR: "Saisir la température la plus élevée prévue (max 70 °C)",
							svSE: "Ange den högsta förväntade temperaturen (max 70 °C)",
						}),
					),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: () => modal.close(),
						styles: ["is-primary"],
					},
					locale.strings.done,
				),
			],
		});
	};

	const field = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: "Temperature alarm",
				deDE: "Temperaturalarm",
				frFR: "Alarme de température",
				svSE: "Temperaturelarm",
			}),
		),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOff.element),
			Bulma.control(
				{},
				Bulma.button(
					{
						onClick: openEditModal,
						styles: ["is-primary"],
					},
					locale.strings.edit,
				),
			),
			Bulma.control(
				{},
				Bulma.input({
					type: "text",
					value: `${sp.TemperatureRangeAlarm.Lower} - ${sp.TemperatureRangeAlarm.Upper} °C`,
					styles: ["is-static"],
				}).element,
			),
		),
	);

	return {
		element: field,
		isOn: () => onOff.getValue() === "on",
		getLower: () => +lower.getValue(),
		getUpper: () => +upper.getValue(),
	};
}

/**
 * @param {Smartplug} sp
 */
function timerField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const onOff = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(
				locale.strings.on,
				"on",
				isSubscribed && sp.Timer.Enabled,
			),
			Bulma.option(
				locale.strings.off,
				"off",
				!isSubscribed || !sp.Timer.Enabled,
			),
		],
	});

	const action = Bulma.select({
		options: [
			Bulma.option(locale.strings.turnOn, "on"),
			Bulma.option(locale.strings.turnOff, "off"),
		],
	});

	const time = Bulma.timeInput();

	const days = [
		locale.strings.monday,
		locale.strings.tuesday,
		locale.strings.wednesday,
		locale.strings.thursday,
		locale.strings.friday,
		locale.strings.saturday,
		locale.strings.sunday,
	].map((day) =>
		Bulma.checkbox({
			text: day.substring(0, 2),
		}),
	);

	const addRule = () => {
		const [hour, minute] = time.getUtc().split(":");
		const dayIndices = days
			.map((checkbox, i) => (checkbox.isChecked() ? (i + 1) % 7 : -1))
			.filter((i) => i >= 0);

		const a = action.getValue();
		if (a !== "on" && a !== "off") throw `invalid action: ${action}`;

		table.insertRow(new TimerRuleRow(table, +hour, +minute, dayIndices, a));
		modal.close();
	};

	const openNewRuleModal = () => {
		modal.open({
			header: locale.string({
				enGB: "New timer rule",
				deDE: "Neue Timer-Regel",
				frFR: "Nouvelle règle pour le minuteur",
				svSE: "Ny timerregel",
			}),
			body: [
				Bulma.field(
					{
						styles: ["is-grouped"],
					},
					Bulma.field(
						{},
						Bulma.label(
							locale.string({
								enGB: "Action",
								deDE: "Aktion",
								frFR: "Action",
								svSE: "Åtgärd",
							}),
						),
						Bulma.control({}, action.element),
					),
					Bulma.field(
						{},
						Bulma.label(
							locale.string({
								enGB: "Time",
								deDE: "Zeit",
								frFR: "Temps",
								svSE: "Tid",
							}),
						),
						Bulma.control({}, time.element),
					),
				),
				Bulma.field(
					{},
					Bulma.label(
						locale.string({
							enGB: "Days",
							deDE: "Tage",
							frFR: "Jours",
							svSE: "Dagar",
						}),
					),
					Bulma.control(
						{},
						Bulma.checkboxes(...days.map((day) => day.element)),
					),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: addRule,
						styles: ["is-primary"],
					},
					locale.strings.add,
				),
				Bulma.button(
					{
						onClick: () => modal.close(),
						styles: ["is-danger"],
					},
					locale.strings.cancel,
				),
			],
		});
	};

	const table = new TimerRulesTable(sp);

	const field = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: "Timer",
				deDE: "Timer",
				frFR: "Minuteur",
				svSE: "Timer",
			}),
		),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOff.element),
			Bulma.control(
				{},
				Bulma.button(
					{
						onClick: openNewRuleModal,
						styles: ["is-primary"],
					},
					locale.string({
						enGB: "Add rule",
						deDE: "Regel hinzufügen",
						frFR: "Ajouter une règle",
						svSE: "Lägg till regel",
					}),
				),
			),
		),
		table.element,
	);

	return {
		element: field,
		getOffSchedules: () => table.offSchedules,
		getOnSchedules: () => table.onSchedules,
		isOn: () => onOff.getValue() === "on",
	};
}

/**
 * @param {Smartplug} sp
 */
function thermostatField(sp) {
	const isSubscribed =
		sp.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(sp.Subscription.ExpiresAt).getTime();

	const onOff = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(
				locale.strings.on,
				"on",
				isSubscribed && sp.Thermostat.Enabled,
			),
			Bulma.option(
				locale.strings.off,
				"off",
				!isSubscribed || !sp.Thermostat.Enabled,
			),
		],
	});

	const action = Bulma.select({
		options: [
			Bulma.option(locale.strings.turnOn, "on"),
			Bulma.option(locale.strings.turnOff, "off"),
		],
	});

	const direction = Bulma.select({
		options: [
			Bulma.option("Above", "above"),
			Bulma.option("Below", "below"),
		],
	});

	const temperature = Bulma.input({
		type: "text",
	});

	const days = [
		locale.strings.monday,
		locale.strings.tuesday,
		locale.strings.wednesday,
		locale.strings.thursday,
		locale.strings.friday,
		locale.strings.saturday,
		locale.strings.sunday,
	].map((day) =>
		Bulma.checkbox({
			text: day.substring(0, 2),
		}),
	);

	const addRule = () => {
		const d = direction.getValue();
		if (d !== "above" && d !== "below")
			throw `invalid thermostat rule direction: ${d}`;

		const a = action.getValue();
		if (a !== "on" && a !== "off") throw `invalid action: ${action}`;

		const dayIndices = days
			.map((checkbox, i) => (checkbox.isChecked() ? (i + 1) % 7 : -1))
			.filter((i) => i >= 0);

		table.insertRow(
			new ThermostatRuleRow(
				table,
				d,
				+temperature.getValue(),
				a,
				dayIndices,
			),
		);
		modal.close();
	};

	const openNewRuleModal = () => {
		modal.open({
			header: locale.string({
				enGB: "New thermostat rule",
				deDE: "Neue Thermostatregel",
				frFR: "Nouvelle règle du thermostat",
				svSE: "Ny termostatregel",
			}),
			body: [
				Bulma.field(
					{},
					Bulma.label(
						locale.string({
							enGB: "Action",
							deDE: "Aktion",
							frFR: "Action",
							svSE: "Åtgärd",
						}),
					),
					Bulma.control({}, action.element),
				),
				Bulma.label(
					locale.string({
						enGB: "Temperature (-20 - 70 °C)",
						deDE: "Temperatur (-20 - 70 °C)",
						frFR: "Température (-20 - 70 °C)",
						svSE: "Temperatur (-20 - 70 °C)",
					}),
				),
				Bulma.field(
					{
						styles: ["is-grouped"],
					},
					Bulma.control({}, direction.element),
					Bulma.control({}, temperature.element),
				),
				Bulma.field(
					{},
					Bulma.label(
						locale.string({
							enGB: "Days",
							deDE: "Tage",
							frFR: "Jours",
							svSE: "Dagar",
						}),
					),
					Bulma.control(
						{},
						Bulma.checkboxes(...days.map((day) => day.element)),
					),
				),
			],
			footer: [
				Bulma.button(
					{
						onClick: addRule,
						styles: ["is-primary"],
					},
					locale.strings.add,
				),
				Bulma.button(
					{
						onClick: () => modal.close(),
						styles: ["is-danger"],
					},
					locale.strings.cancel,
				),
			],
		});
	};

	const table = new ThermostatRulesTable(sp);

	const field = Bulma.field(
		{},
		Bulma.label(
			locale.string({
				enGB: "Thermostat",
				deDE: "Thermostat",
				frFR: "Thermostat",
				svSE: "Termostat",
			}),
		),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOff.element),
			Bulma.control(
				{},
				Bulma.button(
					{
						onClick: openNewRuleModal,
						styles: ["is-primary"],
					},
					locale.string({
						enGB: "Add rule",
						deDE: "Regel hinzufügen",
						frFR: "Ajouter une règle",
						svSE: "Lägg till regel",
					}),
				),
			),
		),
		table.element,
	);

	return {
		element: field,
		getTriggers: () => table.triggers,
		isOn: () => onOff.getValue() === "on",
	};
}

class TimerRulesTable {
	/**
	 * @type {TimerRuleRow[]}
	 */
	rows = [];

	/**
	 * @param {Smartplug} sp
	 */
	constructor(sp) {
		if (!sp.Timer.On) sp.Timer.On = [];
		sp.Timer.On.forEach((cron) =>
			this.rows.push(TimerRuleRow.fromCron(this, cron, "on")),
		);

		if (!sp.Timer.Off) sp.Timer.Off = [];
		sp.Timer.Off.forEach((cron) =>
			this.rows.push(TimerRuleRow.fromCron(this, cron, "off")),
		);

		this.rows.sort(TimerRuleRow.ascending);

		this.element = Bulma.table({
			classList: ["is-fullwidth"],
			body: this.rows.map((row) => row.element),
		});
	}

	get offSchedules() {
		return this.rows
			.filter((row) => row.action === "off")
			.map((row) => row.schedule);
	}

	get onSchedules() {
		return this.rows
			.filter((row) => row.action === "on")
			.map((row) => row.schedule);
	}

	/**
	 * @param {TimerRuleRow} row
	 */
	insertRow(row) {
		// calculate index
		let i = 0;
		while (this.rows[i] && TimerRuleRow.ascending(this.rows[i], row) < 1)
			i++;

		// update model
		this.rows.splice(i, 0, row);

		// update view
		this.element.insertRow(i).replaceWith(row.element);
	}

	/**
	 * @param {TimerRuleRow} row
	 */
	removeRow(row) {
		const i = this.rows.indexOf(row);
		this.rows.splice(i, 1);
		row.element.remove();
	}
}

class TimerRuleRow {
	/**
	 * @param {TimerRulesTable} parent
	 * @param {number} hour
	 * @param {number} minute
	 * @param {number[]} days
	 * @param {"on"|"off"} action
	 */
	constructor(parent, hour, minute, days, action) {
		this.hour = hour;
		this.minute = (Math.ceil(minute / 5) * 5) % 60;
		this.days = days;
		this.action = action;

		const deleteIcon = MaterialDesign.icon({
			id: "close",
			onClick: () => parent.removeRow(this),
		});

		const date = new Date();
		date.setUTCHours(this.hour);
		date.setUTCMinutes(this.minute);

		const time = locale.kitchenTimeString(date);

		this.element = Bulma.tableRow(
			{},
			Bulma.tableDataCell(time),
			Bulma.tableDataCell(
				`${days.map((day) => indexToWeekday(day).substring(0, 2)).join(" ")}`,
			),
			Bulma.tableDataCell(action),
			Bulma.tableDataCell(deleteIcon),
		);
	}

	get schedule() {
		return `${this.minute} ${this.hour} ${this.days.join(",")}`;
	}

	/**
	 * @param {TimerRuleRow} a
	 * @param {TimerRuleRow} b
	 * @returns {number}
	 */
	static ascending(a, b) {
		if (a.hour !== b.hour) return a.hour - b.hour;
		if (a.minute !== b.minute) return a.minute - b.minute;
		return a.action === "on" ? -1 : 1;
	}

	/**
	 * @param {TimerRulesTable} table
	 * @param {string} cron // minute hour days
	 * @param {"on"|"off"} action
	 * @returns {TimerRuleRow}
	 */
	static fromCron(table, cron, action) {
		const [minute, hour, days] = cron.split(" ");
		return new TimerRuleRow(
			table,
			+hour,
			+minute,
			days.split(",").map(Number),
			action,
		);
	}
}

class ThermostatRulesTable {
	/**
	 * @type {ThermostatRuleRow[]}
	 */
	rows = [];

	/**
	 * @param {Smartplug} sp
	 */
	constructor(sp) {
		if (!sp.Thermostat.Triggers) sp.Thermostat.Triggers = [];
		sp.Thermostat.Triggers.forEach((trigger) =>
			this.rows.push(
				new ThermostatRuleRow(
					this,
					trigger.Above ? "above" : "below",
					trigger.Temperature,
					trigger.SetPowerEnabled ? "on" : "off",
					trigger.Days,
				),
			),
		);

		this.rows.sort(ThermostatRuleRow.ascending);

		this.element = Bulma.table({
			classList: ["is-fullwidth"],
			body: this.rows.map((row) => row.element),
		});
	}

	get triggers() {
		return this.rows.map((row) => row.toTrigger());
	}

	/**
	 * @param {ThermostatRuleRow} row
	 */
	insertRow(row) {
		// calculate index
		let i = 0;
		while (
			this.rows[i] &&
			ThermostatRuleRow.ascending(this.rows[i], row) < 1
		)
			i++;

		// update model
		this.rows.splice(i, 0, row);

		// update view
		this.element.insertRow(i).replaceWith(row.element);
	}

	/**
	 * @param {ThermostatRuleRow} row
	 */
	removeRow(row) {
		const i = this.rows.indexOf(row);
		this.rows.splice(i, 1);
		row.element.remove();
	}
}

class ThermostatRuleRow {
	/**
	 * @param {ThermostatRulesTable} parent
	 * @param {"above"|"below"} direction
	 * @param {number} temperature
	 * @param {"on"|"off"} action
	 * @param {number[]} days
	 */
	constructor(parent, direction, temperature, action, days) {
		this.direction = direction;
		this.temperature = temperature;
		this.action = action;
		this.days = days;

		const deleteIcon = MaterialDesign.icon({
			id: "close",
			onClick: () => parent.removeRow(this),
		});

		this.element = Bulma.tableRow(
			{},
			Bulma.tableDataCell(direction),
			Bulma.tableDataCell(temperature.toString()),
			Bulma.tableDataCell(action),
			Bulma.tableDataCell(
				`${days.map((day) => indexToWeekday(day).substring(0, 2)).join(" ")}`,
			),
			Bulma.tableDataCell(deleteIcon),
		);
	}

	toTrigger() {
		return {
			Days: this.days,
			Above: this.direction === "above",
			Temperature: this.temperature,
			SetPowerEnabled: this.action === "on",
		};
	}

	/**
	 * @param {ThermostatRuleRow} a
	 * @param {ThermostatRuleRow} b
	 * @returns {number}
	 */
	static ascending(a, b) {
		return a.temperature - b.temperature;
	}
}

export { smartplugSettingsPage };
